import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Wrench, ChevronDown, ChevronUp, Image, Clock, Mail, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { usePermissions } from '../hooks/usePermissions';

import { getBackendUrl } from '../utils/getBackendUrl';
const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

const Projects = ({ user }) => {
  const navigate = useNavigate();
  const { can, loading: permissionsLoading } = usePermissions();
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [projectNotes, setProjectNotes] = useState({});  // Store notes count per project
  const [projectHours, setProjectHours] = useState({});  // Store hours per project (admin only)
  const [hoursBreakdown, setHoursBreakdown] = useState(null); // { project, logs } for breakdown dialog
  const [breakdownLoading, setBreakdownLoading] = useState(false);
  const [newClient, setNewClient] = useState(null); // {name,email,phone,company,password} when adding inline
  const [dlgClientSort, setDlgClientSort] = useState(() => localStorage.getItem('projects-client-sort') || 'recent');

  const clientRecentsKey = `projects-client-recents-${user?.id}`;
  const getClientRecents = () => {
    try { return JSON.parse(localStorage.getItem(clientRecentsKey)) || {}; } catch { return {}; }
  };
  const recordClientRecent = (clientId) => {
    const r = getClientRecents();
    r[clientId] = Date.now();
    localStorage.setItem(clientRecentsKey, JSON.stringify(r));
  };
  const changeClientSort = (mode) => {
    setDlgClientSort(mode);
    localStorage.setItem('projects-client-sort', mode);
  };
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    status: 'active',
    category: '',
    timeline: '',
    assigned_users: [],
    assigned_clients: []
  });
  const [editingProject, setEditingProject] = useState(null);
  const [clients, setClients] = useState([]);
  const clientRecents = getClientRecents();
  const sortedClients = [...clients].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  if (dlgClientSort === 'recent') {
    sortedClients.sort((a, b) => (clientRecents[b.id] || 0) - (clientRecents[a.id] || 0));
  }
  const [categories, setCategories] = useState([]);
  const [clientsMap, setClientsMap] = useState({});  // Map of client ID to client name
  const [expandedPastProjects, setExpandedPastProjects] = useState({});  // Track which client's past projects are expanded
  const [photoNotificationStatus, setPhotoNotificationStatus] = useState({});  // Track photo notification status per project
  const [isNotificationDialogOpen, setIsNotificationDialogOpen] = useState(false);
  const [notificationProject, setNotificationProject] = useState(null);
  const [customMessage, setCustomMessage] = useState('');
  const [isSendingNotification, setIsSendingNotification] = useState(false);
  const [heroThumbs, setHeroThumbs] = useState({}); // { projectId: filePath }
  const [clientSort, setClientSort] = useState(() => {
    try { return localStorage.getItem('projects_client_sort') || 'az'; }
    catch { return 'az'; }
  });

  const fetchHeroThumbs = async () => {
    try {
      const { data } = await axios.get(`${API}/project-hero-thumbnails`);
      setHeroThumbs(data || {});
    } catch {
      /* silent — thumbnails are non-critical */
    }
  };

  useEffect(() => {
    fetchProjects();
    fetchUsers();
    fetchClients();  // Fetch clients to display on project cards
    fetchHeroThumbs();
    
    // Fetch project hours for admin
    if ((user.role === 'admin' || user.role === 'supervisor')) {
      fetchProjectHours();
    }
  }, [user.role]);
  
  // Refresh unread counts when projects data is available or component becomes visible
  useEffect(() => {
    if (projects.length > 0) {
      fetchProjectNotes(projects);
    }
    
    // Also refresh when page becomes visible (user switches tabs/windows)
    const handleVisibilityChange = () => {
      if (!document.hidden && projects.length > 0) {
        fetchProjectNotes(projects);
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [projects]);

  // Fetch clients and categories when dialog opens
  useEffect(() => {
    if (isDialogOpen) {
      fetchClients();
      fetchCategories();
    }
  }, [isDialogOpen]);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`);
      if (Array.isArray(response.data)) {
        setProjects(response.data);
        // Fetch notes count for each project
        fetchProjectNotes(response.data);
        // Fetch hours for admin
        if ((user.role === 'admin' || user.role === 'supervisor')) {
          fetchProjectHours();
        }
      } else {
        console.error('Invalid projects response:', response.data);
        setProjects([]);
      }
    } catch (error) {
      console.error('Failed to fetch projects:', error);
      toast.error('Failed to fetch projects');
      setProjects([]);
    }
  };

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      if (Array.isArray(response.data)) {
        const clientUsers = response.data.filter(u => u.role === 'client');
        setClients(clientUsers);
        
        // Create a map of client ID to client name for easy lookup
        const map = {};
        clientUsers.forEach(client => {
          map[client.id] = client.name;
        });
        setClientsMap(map);
      } else {
        setClients([]);
        setClientsMap({});
      }
    } catch (error) {
      console.error('Failed to fetch clients:', error);
      setClients([]);
      setClientsMap({});
    }
  };

  const openHoursBreakdown = async (project) => {
    setBreakdownLoading(true);
    setHoursBreakdown({ project, logs: [] });
    try {
      const res = await axios.get(`${API}/admin/projects/${project.id}/timelogs`);
      setHoursBreakdown({ project, logs: res.data || [] });
    } catch (error) {
      console.error('Failed to fetch project time logs:', error);
    } finally {
      setBreakdownLoading(false);
    }
  };

  const fetchProjectHours = async () => {
    if (user.role !== 'admin') return;
    
    try {
      const response = await axios.get(`${API}/admin/projects/hours`);
      setProjectHours(response.data || {});
    } catch (error) {
      console.error('Failed to fetch project hours:', error);
      setProjectHours({});
    }
  };

  const fetchPhotoNotificationStatus = async (projectId) => {
    if (user.role !== 'admin') return;
    
    try {
      const response = await axios.get(`${API}/photos/${projectId}/notification-status`);
      setPhotoNotificationStatus(prev => ({
        ...prev,
        [projectId]: response.data
      }));
    } catch (error) {
      console.error('Failed to fetch photo notification status:', error);
    }
  };

  const sendManualPhotoNotification = async () => {
    if (!notificationProject) return;
    
    setIsSendingNotification(true);
    try {
      const response = await axios.post(`${API}/photos/send-project-update`, {
        project_id: notificationProject.id,
        custom_message: customMessage.trim() || undefined
      });
      
      toast.success(response.data.message);
      
      // Refresh notification status for this project
      await fetchPhotoNotificationStatus(notificationProject.id);
      
      // Close dialog and reset state
      setIsNotificationDialogOpen(false);
      setNotificationProject(null);
      setCustomMessage('');
      
    } catch (error) {
      console.error('Failed to send photo notification:', error);
      toast.error(error.response?.data?.detail || 'Failed to send photo notification');
    } finally {
      setIsSendingNotification(false);
    }
  };

  const openNotificationDialog = (project) => {
    setNotificationProject(project);
    setCustomMessage('');
    setIsNotificationDialogOpen(true);
    // Fetch current status when dialog opens
    fetchPhotoNotificationStatus(project.id);
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/settings/categories`);
      if (response.data && Array.isArray(response.data.categories)) {
        setCategories(response.data.categories);
      } else {
        setCategories([]);
      }
    } catch (error) {
      console.error('Failed to fetch categories:', error);
      setCategories([]);
    }
  };

  const fetchProjectNotes = async (projects) => {
    if (!Array.isArray(projects)) {
      setProjectNotes({});
      return;
    }
    
    // Fetch unread notes counts for all projects
    try {
      const response = await axios.get(`${API}/notes/unread-counts`);
      setProjectNotes(response.data);
    } catch (error) {
      console.error('Failed to fetch unread notes counts:', error);
      setProjectNotes({});
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      if (Array.isArray(response.data)) {
        setUsers(response.data);
      } else {
        console.error('Invalid users response:', response.data);
        setUsers([]);
      }
    } catch (error) {
      console.error('Failed to fetch users:', error);
      setUsers([]);
    }
  };

  const handleOpenDialog = (project = null) => {
    if (project) {
      // Editing mode
      setEditingProject(project);
      setFormData({
        name: project.name,
        description: project.description,
        status: project.status,
        category: project.category || '',
        timeline: project.timeline || '',
        assigned_users: project.assigned_users || [],
        assigned_clients: project.assigned_clients || []
      });
    } else {
      // Creating mode
      setEditingProject(null);
      setFormData({
        name: '',
        description: '',
        status: 'active',
        category: '',
        timeline: '',
        assigned_users: [],
        assigned_clients: []
      });
    }
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setEditingProject(null);
    setNewClient(null);
    setFormData({
      name: '',
      description: '',
      status: 'active',
      category: '',
      timeline: '',
      assigned_users: [],
      assigned_clients: []
    });
  };

  const handleCreateProject = async (e) => {
    e.preventDefault();
    try {
      let projectData = formData;
      if (newClient) {
        if (!newClient.name.trim() || !newClient.email.trim() || !newClient.password.trim()) {
          toast.error('New client needs a name, email and password');
          return;
        }
        const res = await axios.post(`${API}/admin/users`, {
          name: newClient.name.trim(),
          email: newClient.email.trim(),
          password: newClient.password,
          role: 'client',
          phone: newClient.phone.trim() || null,
          company: newClient.company.trim() || null,
        });
        projectData = { ...formData, assigned_clients: [res.data.id, ...formData.assigned_clients] };
        toast.success(`Client "${res.data.name}" added — they're now on the Team page`);
      }
      if (editingProject) {
        // Update existing project
        await axios.put(`${API}/projects/${editingProject.id}`, projectData);
        toast.success('Project updated successfully!');
      } else {
        // Create new project
        await axios.post(`${API}/projects`, projectData);
        toast.success('Project created successfully!');
      }
      handleCloseDialog();
      fetchProjects();
      fetchClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save project');
    }
  };

  const handleDeleteProject = async (projectId) => {
    if (!window.confirm('Are you sure you want to delete this project?')) return;
    
    try {
      await axios.delete(`${API}/projects/${projectId}`);
      toast.success('Project deleted successfully');
      fetchProjects();
    } catch (error) {
      toast.error('Failed to delete project');
    }
  };

  // Small thumbnail next to each project name. Resolves the relative
  // file_path returned by /api/project-hero-thumbnails into a full URL.
  const HeroThumb = ({ projectId, name }) => {
    const path = heroThumbs[projectId];
    if (!path) {
      const letter = (name || '?').trim().charAt(0).toUpperCase();
      return (
        <div
          className="w-10 h-10 rounded-lg flex items-center justify-center bg-gradient-to-br from-blue-400 to-blue-600 text-white text-sm font-bold flex-shrink-0 shadow-sm"
          aria-hidden
        >
          {letter}
        </div>
      );
    }
    const src = path.startsWith('http') ? path : `${BACKEND_URL}${path}`;
    return (
      <img
        src={src}
        alt=""
        loading="lazy"
        className="w-10 h-10 rounded-lg object-cover flex-shrink-0 shadow-sm border border-gray-200"
      />
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-20 pt-20" data-testid="projects-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-2">Projects</h1>
          <p className="text-sm sm:text-base text-gray-600">Manage your construction projects</p>
        </div>
        {(user.role === 'admin' || user.role === 'supervisor' || user.role === 'employee') && (user.role === 'employee' || permissionsLoading || can('projects', 'create')) && (
          <div className="flex flex-col sm:flex-row gap-2">
            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
              <DialogTrigger asChild>
                <Button 
                  className="bg-red-600 hover:bg-red-700 text-white font-semibold shadow-md min-h-[48px] px-3 sm:px-4 text-sm sm:text-base" 
                  data-testid="create-project-btn"
                  onClick={() => handleOpenDialog()}
                >
                  <Plus className="w-4 h-4 mr-1 sm:mr-2 flex-shrink-0" />
                  <span className="whitespace-nowrap">New Project</span>
                </Button>
              </DialogTrigger>
            <DialogContent className="bg-white text-gray-900 border-gray-200 max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingProject ? 'Edit Project' : 'Create New Project'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateProject} className="space-y-4" data-testid="create-project-form">
                {/* Primary Client Selection - Top of Form */}
                <div>
                    <div className="flex items-center justify-between mb-1">
                      <Label htmlFor="primary-client">Select Client</Label>
                      <div className="flex rounded-lg border border-gray-300 overflow-hidden">
                        <button
                          type="button"
                          onClick={() => changeClientSort('recent')}
                          className={`px-2.5 py-1 text-xs font-bold ${dlgClientSort === 'recent' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600'}`}
                          data-testid="client-sort-recent-btn"
                        >
                          Recent
                        </button>
                        <button
                          type="button"
                          onClick={() => changeClientSort('alpha')}
                          className={`px-2.5 py-1 text-xs font-bold ${dlgClientSort === 'alpha' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600'}`}
                          data-testid="client-sort-alpha-btn"
                        >
                          A–Z
                        </button>
                      </div>
                    </div>
                    <Select
                      value={newClient ? 'none' : (formData.assigned_clients[0] || 'none')}
                      onValueChange={(value) => {
                        setNewClient(null);
                        if (value !== 'none') recordClientRecent(value);
                        // Set this as the primary client (first in array)
                        const otherClients = formData.assigned_clients.slice(1);
                        if (value === 'none') {
                          setFormData({ 
                            ...formData, 
                            assigned_clients: otherClients 
                          });
                        } else {
                          setFormData({ 
                            ...formData, 
                            assigned_clients: [value, ...otherClients] 
                          });
                        }
                      }}
                    >
                      <SelectTrigger className="mt-1" id="primary-client">
                        <SelectValue placeholder="Select a client" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {sortedClients.map((client) => (
                          <SelectItem key={client.id} value={client.id}>
                            {client.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {(user.role === 'admin' || user.role === 'supervisor') && !newClient && (
                      <Button
                        type="button"
                        onClick={() => {
                          setNewClient({ name: '', email: '', phone: '', company: '', password: '' });
                          setFormData({ ...formData, assigned_clients: formData.assigned_clients.slice(1) });
                        }}
                        className="w-full mt-2 bg-blue-600 hover:bg-blue-700 text-white font-bold"
                        data-testid="add-new-client-btn"
                      >
                        <Plus className="w-4 h-4 mr-2" /> Add New Client
                      </Button>
                    )}
                    {newClient && (
                      <div className="mt-3 p-3 rounded-lg border-2 border-blue-200 bg-blue-50 space-y-3" data-testid="new-client-form">
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-bold text-blue-900">New Client Details</p>
                          <button type="button" onClick={() => setNewClient(null)} className="text-xs font-bold text-blue-700 underline" data-testid="cancel-new-client-btn">Cancel</button>
                        </div>
                        <div>
                          <Label htmlFor="nc-name">Name *</Label>
                          <Input id="nc-name" data-testid="new-client-name-input" className="mt-1 bg-white" value={newClient.name}
                            onChange={(e) => setNewClient({ ...newClient, name: e.target.value })} required />
                        </div>
                        <div>
                          <Label htmlFor="nc-email">Email *</Label>
                          <Input id="nc-email" type="email" data-testid="new-client-email-input" className="mt-1 bg-white" value={newClient.email}
                            onChange={(e) => setNewClient({ ...newClient, email: e.target.value })} required />
                        </div>
                        <div>
                          <Label htmlFor="nc-password">Password * <span className="text-xs text-gray-500 font-normal">(for their login)</span></Label>
                          <Input id="nc-password" data-testid="new-client-password-input" className="mt-1 bg-white" value={newClient.password}
                            onChange={(e) => setNewClient({ ...newClient, password: e.target.value })} required />
                        </div>
                        <div>
                          <Label htmlFor="nc-phone">Phone</Label>
                          <Input id="nc-phone" type="tel" data-testid="new-client-phone-input" className="mt-1 bg-white" value={newClient.phone}
                            onChange={(e) => setNewClient({ ...newClient, phone: e.target.value })} />
                        </div>
                        <div>
                          <Label htmlFor="nc-company">Company</Label>
                          <Input id="nc-company" data-testid="new-client-company-input" className="mt-1 bg-white" value={newClient.company}
                            onChange={(e) => setNewClient({ ...newClient, company: e.target.value })} />
                        </div>
                        <p className="text-xs text-blue-800">This client will be created when you save the project and will appear on the Team page under Clients.</p>
                      </div>
                    )}
                  </div>
                <div>
                  <Label htmlFor="project-name">Project Name</Label>
                  <Input
                    id="project-name"
                    data-testid="project-name-input"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="project-description">Description</Label>
                  <Textarea
                    id="project-description"
                    data-testid="project-description-input"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    required
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="project-status">Status</Label>
                  <Select
                    value={formData.status}
                    onValueChange={(value) => setFormData({ ...formData, status: value })}
                  >
                    <SelectTrigger className="mt-1" data-testid="project-status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="project-category">Category (Optional)</Label>
                  <Select
                    value={formData.category}
                    onValueChange={(value) => setFormData({ ...formData, category: value })}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select a category" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.length > 0 ? (
                        categories.map((cat) => (
                          <SelectItem key={cat} value={cat.toLowerCase().replace(/\s+/g, '_')}>
                            {cat}
                          </SelectItem>
                        ))
                      ) : (
                        <>
                          <SelectItem value="residential">Residential</SelectItem>
                          <SelectItem value="commercial">Commercial</SelectItem>
                          <SelectItem value="renovation">Renovation</SelectItem>
                          <SelectItem value="new_construction">New Construction</SelectItem>
                          <SelectItem value="remodel">Remodel</SelectItem>
                          <SelectItem value="repair">Repair</SelectItem>
                          <SelectItem value="maintenance">Maintenance</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </>
                      )}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="project-timeline">Timeline</Label>
                  <Input
                    id="project-timeline"
                    data-testid="project-timeline-input"
                    placeholder="e.g., 3 months"
                    value={formData.timeline}
                    onChange={(e) => setFormData({ ...formData, timeline: e.target.value })}
                    className="mt-1"
                  />
                </div>
                <Button type="submit" className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold shadow-md min-h-[48px]" data-testid="submit-project-btn">
                  {editingProject ? 'Update Project' : 'Create Project'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>

          {/* Manual Photo Notification Dialog */}
          <Dialog open={isNotificationDialogOpen} onOpenChange={setIsNotificationDialogOpen}>
            <DialogContent className="bg-white text-gray-900 border-gray-200">
              <DialogHeader>
                <DialogTitle>Send Photo Update Notification</DialogTitle>
              </DialogHeader>
              
              {notificationProject && (
                <div className="space-y-4">
                  <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <h4 className="font-medium text-blue-900 mb-2">{notificationProject.name}</h4>
                    {photoNotificationStatus[notificationProject.id] && (
                      <div className="text-sm text-blue-700 space-y-1">
                        <p>• Total photos: {photoNotificationStatus[notificationProject.id].total_photos}</p>
                        <p>• New photos to notify: {photoNotificationStatus[notificationProject.id].new_photos_count}</p>
                        <p>• Assigned clients: {photoNotificationStatus[notificationProject.id].assigned_clients}</p>
                      </div>
                    )}
                  </div>

                  {photoNotificationStatus[notificationProject.id]?.new_photos_count > 0 ? (
                    <>
                      <div>
                        <Label htmlFor="custom-message">Custom Message (Optional)</Label>
                        <Textarea
                          id="custom-message"
                          value={customMessage}
                          onChange={(e) => setCustomMessage(e.target.value)}
                          placeholder="Add a personal message about this update..."
                          className="mt-1 bg-white border-2 border-gray-400 text-gray-900 font-semibold placeholder:text-gray-400"
                          rows={4}
                        />
                        <p className="text-sm text-gray-500 mt-1">
                          Leave empty for a standard notification message.
                        </p>
                      </div>

                      <div className="flex gap-2 justify-end">
                        <Button
                          variant="outline"
                          onClick={() => setIsNotificationDialogOpen(false)}
                          disabled={isSendingNotification}
                        >
                          Cancel
                        </Button>
                        <Button
                          onClick={sendManualPhotoNotification}
                          disabled={isSendingNotification}
                          className="bg-orange-600 hover:bg-orange-700 text-white"
                        >
                          {isSendingNotification ? (
                            <>Sending...</>
                          ) : (
                            <>
                              <Mail className="w-4 h-4 mr-2" />
                              Send Notification
                            </>
                          )}
                        </Button>
                      </div>
                    </>
                  ) : (
                    <div className="text-center py-6">
                      <Mail className="w-12 h-12 mx-auto text-gray-400 mb-3" />
                      <p className="text-gray-600">No new photos to notify about.</p>
                      <p className="text-sm text-gray-500 mt-1">
                        All photos have already been sent in previous notifications.
                      </p>
                      <Button
                        variant="outline"
                        onClick={() => setIsNotificationDialogOpen(false)}
                        className="mt-4"
                      >
                        Close
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </DialogContent>
          </Dialog>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {(() => {
          // Group projects by client
          const projectsByClient = {};
          const noClientProjects = [];
          
          projects.forEach(project => {
            const clientId = project.assigned_clients?.[0];
            if (clientId) {
              if (!projectsByClient[clientId]) {
                projectsByClient[clientId] = [];
              }
              projectsByClient[clientId].push(project);
            } else {
              noClientProjects.push(project);
            }
          });

          // Deterministic color palette per client (tinted backgrounds)
          const CARD_TINTS = [
            { bg: 'bg-amber-50', border: 'border-amber-200', chip: 'bg-amber-100 text-amber-800 hover:bg-amber-200 border-amber-300' },
            { bg: 'bg-sky-50', border: 'border-sky-200', chip: 'bg-sky-100 text-sky-800 hover:bg-sky-200 border-sky-300' },
            { bg: 'bg-emerald-50', border: 'border-emerald-200', chip: 'bg-emerald-100 text-emerald-800 hover:bg-emerald-200 border-emerald-300' },
            { bg: 'bg-rose-50', border: 'border-rose-200', chip: 'bg-rose-100 text-rose-800 hover:bg-rose-200 border-rose-300' },
            { bg: 'bg-violet-50', border: 'border-violet-200', chip: 'bg-violet-100 text-violet-800 hover:bg-violet-200 border-violet-300' },
            { bg: 'bg-teal-50', border: 'border-teal-200', chip: 'bg-teal-100 text-teal-800 hover:bg-teal-200 border-teal-300' },
            { bg: 'bg-orange-50', border: 'border-orange-200', chip: 'bg-orange-100 text-orange-800 hover:bg-orange-200 border-orange-300' },
            { bg: 'bg-indigo-50', border: 'border-indigo-200', chip: 'bg-indigo-100 text-indigo-800 hover:bg-indigo-200 border-indigo-300' },
          ];
          const tintFor = (id) => {
            let h = 0;
            for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
            return CARD_TINTS[h % CARD_TINTS.length];
          };

          const rawClientEntries = Object.entries(projectsByClient);

          // Sort client entries based on user's pick
          const sortClientEntries = (entries) => {
            const arr = [...entries];
            const statusOf = (p) => (p.status || '').toLowerCase();
            const latestUpdated = (clientProjects) => {
              const times = clientProjects
                .map(p => p.updated_at || p.created_at)
                .filter(Boolean)
                .map(t => new Date(t).getTime())
                .filter(n => !isNaN(n));
              return times.length ? Math.max(...times) : 0;
            };
            const countByStatus = (clientProjects, status) =>
              clientProjects.filter(p => statusOf(p) === status).length;

            if (clientSort === 'az') {
              arr.sort(([aId], [bId]) =>
                (clientsMap[aId] || '').localeCompare(clientsMap[bId] || '')
              );
            } else if (clientSort === 'latest') {
              arr.sort(([, aProjects], [, bProjects]) =>
                latestUpdated(bProjects) - latestUpdated(aProjects)
              );
            } else if (clientSort === 'completed') {
              arr.sort(([, aProjects], [, bProjects]) => {
                const diff = countByStatus(bProjects, 'completed') - countByStatus(aProjects, 'completed');
                if (diff !== 0) return diff;
                return (clientsMap[arr[0]?.[0]] || '').localeCompare('');
              });
            } else if (clientSort === 'in_progress') {
              arr.sort(([, aProjects], [, bProjects]) => {
                const aCount = aProjects.filter(p => ['active', 'in progress', 'in_progress'].includes(statusOf(p))).length;
                const bCount = bProjects.filter(p => ['active', 'in progress', 'in_progress'].includes(statusOf(p))).length;
                return bCount - aCount;
              });
            } else if (clientSort === 'not_completed') {
              arr.sort(([, aProjects], [, bProjects]) => {
                const aOpen = aProjects.filter(p => statusOf(p) !== 'completed').length;
                const bOpen = bProjects.filter(p => statusOf(p) !== 'completed').length;
                return bOpen - aOpen;
              });
            } else if (clientSort === 'pending') {
              arr.sort(([, aProjects], [, bProjects]) =>
                countByStatus(bProjects, 'pending') - countByStatus(aProjects, 'pending')
              );
            }
            return arr;
          };

          const clientEntries = sortClientEntries(rawClientEntries);

          // Aggregate unread count per client (sum of all their projects' unread notes)
          const clientUnread = {};
          clientEntries.forEach(([clientId, clientProjects]) => {
            clientUnread[clientId] = clientProjects.reduce(
              (sum, p) => sum + (projectNotes[p.id] || 0),
              0
            );
          });

          return (
            <>
              {/* Client Shortcut Bar */}
              {clientEntries.length > 0 && (
                <div className="md:col-span-2 lg:col-span-3 -mt-2">
                  <div
                    className="flex flex-wrap items-center gap-2 p-3 bg-white rounded-lg border border-gray-200 shadow-sm"
                    data-testid="client-shortcut-bar"
                  >
                    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider self-center mr-1">
                      Jump to:
                    </span>

                    {/* Sort dropdown */}
                    <select
                      value={clientSort}
                      onChange={(e) => {
                        setClientSort(e.target.value);
                        try { localStorage.setItem('projects_client_sort', e.target.value); } catch {/* ignore */}
                      }}
                      className="ml-auto sm:ml-0 mr-1 px-2 py-1 text-xs font-bold border border-gray-300 rounded-md bg-white text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-400"
                      data-testid="projects-client-sort"
                    >
                      <option value="az">A → Z</option>
                      <option value="latest">Latest accessed</option>
                      <option value="completed">Completed</option>
                      <option value="in_progress">In Progress</option>
                      <option value="pending">Pending</option>
                      <option value="not_completed">Not Complete</option>
                    </select>

                    {clientEntries.map(([clientId]) => {
                      const tint = tintFor(clientId);
                      const name = clientsMap[clientId] || 'Unknown Client';
                      const unread = clientUnread[clientId] || 0;
                      return (
                        <button
                          key={`shortcut-${clientId}`}
                          onClick={() => {
                            const el = document.getElementById(`client-section-${clientId}`);
                            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                          }}
                          className={`relative px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wide border transition-all hover:scale-105 active:scale-95 ${tint.chip}`}
                          data-testid={`client-shortcut-${clientId}`}
                        >
                          {name}
                          {unread > 0 && (
                            <span
                              className="absolute -top-1.5 -right-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold text-white bg-red-600 rounded-full ring-2 ring-white shadow-sm animate-pulse"
                              data-testid={`client-shortcut-alert-${clientId}`}
                            >
                              {unread}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Render client groups */}
              {clientEntries.map(([clientId, clientProjects]) => {
                // Separate current and past projects
                const currentProjects = clientProjects.filter(p => p.status.toLowerCase() !== 'completed');
                const pastProjects = clientProjects.filter(p => p.status.toLowerCase() === 'completed');
                
                // Calculate project counts by status
                const statusCounts = clientProjects.reduce((acc, project) => {
                  const status = project.status.toLowerCase();
                  acc[status] = (acc[status] || 0) + 1;
                  return acc;
                }, {});
                
                return (
                <div
                  key={`client-${clientId}`}
                  id={`client-section-${clientId}`}
                  className={`rounded-lg shadow-md hover:shadow-lg transition-shadow p-6 border scroll-mt-24 ${tintFor(clientId).bg} ${tintFor(clientId).border}`}
                >
                  {/* Client Name Header */}
                  <div className="mb-4">
                    <div className="relative inline-block">
                      <div className="inline-block px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg shadow-md">
                        <p className="text-base font-bold text-white uppercase tracking-wider">
                          {clientsMap[clientId] || 'Unknown Client'}
                        </p>
                      </div>
                      {clientUnread[clientId] > 0 && (
                        <span
                          className="absolute -top-2 -right-2 inline-flex items-center justify-center min-w-[22px] h-[22px] px-1 text-[11px] font-bold text-white bg-red-600 rounded-full ring-2 ring-white shadow-sm animate-pulse"
                          data-testid={`client-header-alert-${clientId}`}
                          title={`${clientUnread[clientId]} unread note${clientUnread[clientId] > 1 ? 's' : ''}`}
                        >
                          {clientUnread[clientId]}
                        </span>
                      )}
                    </div>
                    
                    {/* Project Status Counts */}
                    <div className="flex flex-wrap gap-2 mt-3">
                      {statusCounts.active > 0 && (
                        <div className="flex items-center gap-1.5 px-3 py-1.5 bg-green-50 border border-green-200 rounded-full">
                          <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                          <span className="text-sm font-semibold text-green-700">Active: {statusCounts.active}</span>
                        </div>
                      )}
                      {statusCounts.completed > 0 && (
                        <div className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 border border-blue-200 rounded-full">
                          <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                          <span className="text-sm font-semibold text-blue-700">Completed: {statusCounts.completed}</span>
                        </div>
                      )}
                      {statusCounts.pending > 0 && (
                        <div className="flex items-center gap-1.5 px-3 py-1.5 bg-yellow-50 border border-yellow-200 rounded-full">
                          <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                          <span className="text-sm font-semibold text-yellow-700">Pending: {statusCounts.pending}</span>
                        </div>
                      )}
                      {statusCounts['on hold'] > 0 && (
                        <div className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-full">
                          <div className="w-2 h-2 bg-gray-500 rounded-full"></div>
                          <span className="text-sm font-semibold text-gray-700">On Hold: {statusCounts['on hold']}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Current Projects List */}
                  <div className="space-y-3">
                    {currentProjects.length > 0 ? (
                      currentProjects.map((project, index) => (
                      <div
                        key={project.id}
                        className={`border-l-4 border-blue-500 pl-4 rounded-lg transition-all cursor-pointer py-3 px-3 ${
                          index % 2 === 0 
                            ? 'bg-gray-50 hover:bg-gray-100 hover:shadow-sm' 
                            : 'bg-blue-50/30 hover:bg-blue-50 hover:shadow-sm'
                        }`}
                        onClick={() => navigate(`/projects/${project.id}`)}
                        data-testid={`project-card-${project.id}`}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center gap-2 flex-1">
                            <HeroThumb projectId={project.id} name={project.name} />
                            <h3 className="text-lg font-bold text-gray-900">{project.name}</h3>
                            {projectNotes[project.id] > 0 && (
                              <span className="inline-flex items-center justify-center w-6 h-6 text-xs font-bold text-white bg-red-600 rounded-full">
                                {projectNotes[project.id]}
                              </span>
                            )}
                            {(user.role === 'admin' || user.role === 'supervisor') && projectHours[project.id] && projectHours[project.id].total > 0 && (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-200">
                                <Clock className="w-3 h-3 mr-1" />
                                {projectHours[project.id].total}h
                              </span>
                            )}
                          </div>
                          {(user.role === 'admin' || user.role === 'supervisor') && (
                            <div
                              className="flex gap-1"
                              onClick={(e) => e.stopPropagation()}
                              onPointerDown={(e) => e.stopPropagation()}
                              onTouchStart={(e) => e.stopPropagation()}
                            >
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                  handleOpenDialog(project);
                                }}
                                data-testid={`edit-project-${project.id}`}
                                className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              {/* Manual Photo Notification Button */}
                              {project.assigned_clients && project.assigned_clients.length > 0 && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    openNotificationDialog(project);
                                  }}
                                  data-testid={`notify-project-${project.id}`}
                                  className="text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                                  title="Send photo update to client"
                                >
                                  <Mail className="w-4 h-4" />
                                </Button>
                              )}
                              {(permissionsLoading || can('projects', 'delete')) && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    handleDeleteProject(project.id);
                                  }}
                                  data-testid={`delete-project-${project.id}`}
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          )}
                        </div>
                        <p className="text-gray-600 text-sm mb-2 line-clamp-2">{project.description}</p>
                        
                        {/* Hours breakdown for admin */}
                        {(user.role === 'admin' || user.role === 'supervisor') && projectHours[project.id] && projectHours[project.id].total > 0 && (
                          <div
                            className="mb-2 p-2 bg-green-50 rounded-lg border border-green-200 cursor-pointer hover:bg-green-100 transition-colors"
                            onClick={(e) => { e.stopPropagation(); e.preventDefault(); openHoursBreakdown(project); }}
                            onPointerDown={(e) => e.stopPropagation()}
                            onTouchStart={(e) => e.stopPropagation()}
                            data-testid={`hours-breakdown-open-${project.id}`}
                          >
                            <div className="flex items-center justify-between mb-1">
                              <div className="flex items-center gap-1">
                                <Clock className="w-3 h-3 text-green-700" />
                                <span className="text-xs font-medium text-green-900">Time Logged:</span>
                              </div>
                              <span className="text-[10px] font-bold text-green-700 underline">View breakdown</span>
                            </div>
                            <div className="text-xs text-green-800 space-y-0.5">
                              {projectHours[project.id].employees.map((emp, index) => (
                                <div key={index}>
                                  {emp.name}: {emp.hours}h
                                </div>
                              ))}
                              <div className="font-semibold text-green-900 pt-1 border-t border-green-300">
                                Total: {projectHours[project.id].total}h
                              </div>
                            </div>
                          </div>
                        )}
                        
                        <div className="flex flex-wrap items-center gap-2">
                          {project.category && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200 capitalize">
                              {project.category.replace('_', ' ')}
                            </span>
                          )}
                          <span className={`status-badge status-${project.status.toLowerCase()}`}>
                            {project.status}
                          </span>
                          {project.timeline && (
                            <span className="text-xs text-gray-500">{project.timeline}</span>
                          )}
                        </div>
                        <div className="mt-3">
                          <div className="relative inline-block">
                            <Button
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/projects/${project.id}`);
                              }}
                              className="bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold px-4"
                              data-testid={`details-photos-${project.id}`}
                            >
                              Details / Photos <ArrowRight className="w-3 h-3 ml-1" />
                            </Button>
                            {projectNotes[project.id] > 0 && (
                              <span
                                className="absolute -top-2 -right-2 inline-flex items-center justify-center min-w-[22px] h-[22px] px-1 text-[11px] font-bold text-white bg-red-600 rounded-full ring-2 ring-white shadow-sm animate-pulse"
                                data-testid={`project-alert-${project.id}`}
                                title={`${projectNotes[project.id]} unread note${projectNotes[project.id] > 1 ? 's' : ''}`}
                              >
                                {projectNotes[project.id]}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))
                    ) : (
                      <p className="text-gray-500 text-sm italic">No active projects</p>
                    )}
                  </div>
                  
                  {/* Past Projects Section */}
                  {pastProjects.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setExpandedPastProjects(prev => ({
                            ...prev,
                            [clientId]: !prev[clientId]
                          }));
                        }}
                        className="flex items-center justify-between w-full text-left hover:bg-gray-50 p-2 rounded transition-colors"
                      >
                        <div className="flex items-center gap-2">
                          <Image className="w-4 h-4 text-blue-600" />
                          <span className="text-sm font-semibold text-gray-700">
                            Past Projects with Photos ({pastProjects.length})
                          </span>
                        </div>
                        {expandedPastProjects[clientId] ? (
                          <ChevronUp className="w-4 h-4 text-gray-500" />
                        ) : (
                          <ChevronDown className="w-4 h-4 text-gray-500" />
                        )}
                      </button>
                      
                      {/* Expanded Past Projects */}
                      {expandedPastProjects[clientId] && (
                        <div className="mt-3 space-y-2">
                          {pastProjects.map((project, index) => (
                            <div
                              key={project.id}
                              className="border-l-4 border-green-500 pl-4 rounded-lg transition-all cursor-pointer py-2 px-3 bg-green-50/30 hover:bg-green-50 hover:shadow-sm"
                              onClick={() => navigate(`/projects/${project.id}`)}
                              data-testid={`project-card-${project.id}`}
                            >
                              <div className="flex items-start justify-between mb-2">
                                <div className="flex items-center gap-2 flex-1">
                                  <HeroThumb projectId={project.id} name={project.name} />
                                  <h3 className="text-base font-bold text-gray-900">{project.name}</h3>
                                  {projectNotes[project.id] > 0 && (
                                    <span className="inline-flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-red-600 rounded-full">
                                      {projectNotes[project.id]}
                                    </span>
                                  )}
                                </div>
                                {(user.role === 'admin' || user.role === 'supervisor') && (
                                  <div
                                    className="flex gap-1"
                                    onClick={(e) => e.stopPropagation()}
                                    onPointerDown={(e) => e.stopPropagation()}
                                    onTouchStart={(e) => e.stopPropagation()}
                                  >
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        handleOpenDialog(project);
                                      }}
                                      data-testid={`edit-project-${project.id}`}
                                      className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                    >
                                      <Edit className="w-4 h-4" />
                                    </Button>
                                    {/* Manual Photo Notification Button */}
                                    {project.assigned_clients && project.assigned_clients.length > 0 && (
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          e.preventDefault();
                                          openNotificationDialog(project);
                                        }}
                                        data-testid={`notify-project-${project.id}`}
                                        className="text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                                        title="Send photo update to client"
                                      >
                                        <Mail className="w-4 h-4" />
                                      </Button>
                                    )}
                                    {(permissionsLoading || can('projects', 'delete')) && (
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          e.preventDefault();
                                          handleDeleteProject(project.id);
                                        }}
                                        data-testid={`delete-project-${project.id}`}
                                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                      >
                                        <Trash2 className="w-4 h-4" />
                                      </Button>
                                    )}
                                  </div>
                                )}
                              </div>
                              <p className="text-gray-600 text-sm mb-2 line-clamp-2">{project.description}</p>
                              <div className="flex flex-wrap items-center gap-2">
                                {project.category && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-200 capitalize">
                                    {project.category.replace('_', ' ')}
                                  </span>
                                )}
                                <span className="status-badge status-completed">
                                  Completed
                                </span>
                                {project.timeline && (
                                  <span className="text-xs text-gray-500">{project.timeline}</span>
                                )}
                              </div>
                              <div className="mt-2">
                                <div className="relative inline-block">
                                  <Button
                                    size="sm"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      navigate(`/projects/${project.id}`);
                                    }}
                                    className="bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold px-4"
                                    data-testid={`details-photos-past-${project.id}`}
                                  >
                                    Details / Photos <ArrowRight className="w-3 h-3 ml-1" />
                                  </Button>
                                  {projectNotes[project.id] > 0 && (
                                    <span
                                      className="absolute -top-2 -right-2 inline-flex items-center justify-center min-w-[22px] h-[22px] px-1 text-[11px] font-bold text-white bg-red-600 rounded-full ring-2 ring-white shadow-sm animate-pulse"
                                      data-testid={`project-alert-past-${project.id}`}
                                      title={`${projectNotes[project.id]} unread note${projectNotes[project.id] > 1 ? 's' : ''}`}
                                    >
                                      {projectNotes[project.id]}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                );
              })}
              
              {/* Render projects without clients */}
              {noClientProjects.map((project) => (
                <div
                  key={project.id}
                  className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow p-6 cursor-pointer border border-gray-200"
                  onClick={() => navigate(`/projects/${project.id}`)}
                  data-testid={`project-card-${project.id}`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <HeroThumb projectId={project.id} name={project.name} />
                      <h3 className="text-xl font-bold text-gray-900">{project.name}</h3>
                      {projectNotes[project.id] > 0 && (
                        <span className="inline-flex items-center justify-center w-6 h-6 text-xs font-bold text-white bg-red-600 rounded-full">
                          {projectNotes[project.id]}
                        </span>
                      )}
                    </div>
                    {(user.role === 'admin' || user.role === 'supervisor') && (
                      <div
                        className="flex gap-1"
                        onClick={(e) => e.stopPropagation()}
                        onPointerDown={(e) => e.stopPropagation()}
                        onTouchStart={(e) => e.stopPropagation()}
                      >
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            handleOpenDialog(project);
                          }}
                          data-testid={`edit-project-${project.id}`}
                          className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        {/* Manual Photo Notification Button */}
                        {project.assigned_clients && project.assigned_clients.length > 0 && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              openNotificationDialog(project);
                            }}
                            data-testid={`notify-project-${project.id}`}
                            className="text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                            title="Send photo update to client"
                          >
                            <Mail className="w-4 h-4" />
                          </Button>
                        )}
                        {(permissionsLoading || can('projects', 'delete')) && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              handleDeleteProject(project.id);
                            }}
                            data-testid={`delete-project-${project.id}`}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                  <p className="text-gray-600 mb-4 line-clamp-2">{project.description}</p>
                  <div className="flex flex-wrap items-center gap-2 mb-3">
                    {project.category && (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200 capitalize">
                        {project.category.replace('_', ' ')}
                      </span>
                    )}
                    <span className={`status-badge status-${project.status.toLowerCase()}`}>
                      {project.status}
                    </span>
                    {project.timeline && (
                      <span className="text-sm text-gray-500">{project.timeline}</span>
                    )}
                  </div>
                  <div className="mt-3">
                    <div className="relative inline-block">
                      <Button
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/projects/${project.id}`);
                        }}
                        className="bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold px-4"
                        data-testid={`details-photos-nc-${project.id}`}
                      >
                        Details / Photos <ArrowRight className="w-3 h-3 ml-1" />
                      </Button>
                      {projectNotes[project.id] > 0 && (
                        <span
                          className="absolute -top-2 -right-2 inline-flex items-center justify-center min-w-[22px] h-[22px] px-1 text-[11px] font-bold text-white bg-red-600 rounded-full ring-2 ring-white shadow-sm animate-pulse"
                          data-testid={`project-alert-nc-${project.id}`}
                          title={`${projectNotes[project.id]} unread note${projectNotes[project.id] > 1 ? 's' : ''}`}
                        >
                          {projectNotes[project.id]}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </>
          );
        })()}
      </div>

      {projects.length === 0 && (
        <div className="text-center py-16">
          <p className="text-gray-500 text-lg">No projects yet. Create your first project to get started!</p>
        </div>
      )}

      {/* Project hours breakdown dialog (admin) */}
      <Dialog open={!!hoursBreakdown} onOpenChange={(open) => { if (!open) setHoursBreakdown(null); }}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto bg-white" data-testid="hours-breakdown-dialog">
          <DialogHeader>
            <DialogTitle className="text-gray-900">
              Time Logged — {hoursBreakdown?.project?.name}
            </DialogTitle>
          </DialogHeader>
          {breakdownLoading ? (
            <p className="text-sm text-gray-500 py-6 text-center">Loading entries…</p>
          ) : hoursBreakdown?.logs?.length === 0 ? (
            <p className="text-sm text-gray-500 py-6 text-center">No time entries for this project yet.</p>
          ) : (
            <div className="space-y-2">
              {hoursBreakdown?.logs?.map((log) => (
                <div key={log.id} className="p-3 rounded-lg border border-gray-200 bg-gray-50" data-testid={`breakdown-entry-${log.id}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-bold text-gray-900">{log.user_name}</span>
                    <span className="text-sm font-bold text-green-700">{log.hours != null ? `${log.hours}h` : 'In progress'}</span>
                  </div>
                  <p className="text-xs text-gray-600 mt-0.5">
                    {new Date(log.clock_in).toLocaleDateString('en-CA', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric', timeZone: 'America/Toronto' })}
                    {' · '}
                    {new Date(log.clock_in).toLocaleTimeString('en-CA', { hour: 'numeric', minute: '2-digit', timeZone: 'America/Toronto' })}
                    {log.clock_out ? ` – ${new Date(log.clock_out).toLocaleTimeString('en-CA', { hour: 'numeric', minute: '2-digit', timeZone: 'America/Toronto' })}` : ' – …'}
                  </p>
                  <div className="flex flex-wrap gap-1 mt-1.5">
                    {log.service_type && (
                      <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-blue-100 text-blue-800">{log.service_type}</span>
                    )}
                    {log.no_lunch && (
                      <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">No lunch</span>
                    )}
                    {log.worked_alone && (
                      <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-purple-100 text-purple-800">Worked alone</span>
                    )}
                  </div>
                  {log.notes && (
                    <p className="text-xs text-gray-700 mt-1.5 whitespace-pre-wrap">{log.notes}</p>
                  )}
                </div>
              ))}
              <div className="pt-2 border-t border-gray-300 flex items-center justify-between">
                <span className="text-sm font-semibold text-gray-700">Total</span>
                <span className="text-sm font-bold text-green-700" data-testid="breakdown-total-hours">
                  {Math.round((hoursBreakdown?.logs || []).reduce((s, l) => s + (l.hours || 0), 0) * 100) / 100}h
                </span>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      </div>
    </div>
  );
};

export default Projects;